import type { ReactNode } from "react";
import {
  CalendarClock,
  CalendarOff,
  CalendarX,
  CheckCircle2,
  CloudDownload,
  CloudOff,
  CloudUpload,
  Download,
  Edit2,
  Eraser,
  Image as ImageIcon,
  Key,
  Mail,
  MessageSquare,
  Pause,
  Play,
  Plug,
  RotateCcw,
  Shield,
  ShieldOff,
  Trash2,
  Unplug,
  Upload,
  UserCheck,
  UserMinus,
  UserPlus,
  UserX,
  type LucideIcon,
} from "lucide-react";
import { formatBytes } from "../../../lib/bytes";
import { formatDuration, num } from "../wipe";
import { numericDetail, type ActivityItem } from "./types";

export type RenderedEntry = {
  icon: LucideIcon;
  phrase: ReactNode;
  time: string;
};

const ICON_BY_ACTION: Record<string, LucideIcon> = {
  "project.created": Edit2,
  "project.renamed": Edit2,
  "project.suspended": Pause,
  "project.unsuspended": Play,
  "project.config_updated": Edit2,
  "project.icon_updated": ImageIcon,
  "api_key.rotated": Key,
  "invite.sent": Mail,
  "invite.revoked": Mail,
  "invite.resent": Mail,
  "invite.accepted": UserCheck,
  "team.member_removed": UserMinus,
  "player.updated": UserPlus,
  "player.deleted": UserX,
  // The cloud glyphs, not the snapshot's Download/Upload two rows down: a
  // save is one player's file in a bucket, and a snapshot is the project.
  "player.cloud_save_downloaded": CloudDownload,
  "player.cloud_save_uploaded": CloudUpload,
  "player.cloud_save_deleted": CloudOff,
  "guild.updated": Shield,
  "guild.deleted": ShieldOff,
  "guild.member_promoted": Shield,
  "guild.member_demoted": Shield,
  "guild.leadership_transferred": Shield,
  "guild.member_kicked": UserX,
  "guild.player_unblocked": RotateCcw,
  "chat.message_posted": MessageSquare,
  "chat.message_deleted": MessageSquare,
  "project.snapshot_exported": Download,
  "project.snapshot_applied": Upload,
  "leaderboard.deleted": Trash2,
  // A clock for the two that put a change on the calendar and take it off
  // again, a tick for the cut that landed it, a crossed-out calendar for the
  // cut that refused it — the one entry here nobody was watching happen.
  "leaderboard.pending_scheduled": CalendarClock,
  "leaderboard.pending_cancelled": CalendarOff,
  "leaderboard.pending_applied": CheckCircle2,
  "leaderboard.pending_refused": CalendarX,
  // The same eraser the menu item carries, and for the same reason: a wipe is
  // not a deletion, and `Trash2` two rows up is the project going for good.
  "project.wiped": Eraser,
  // Platform-wide actions: an agent given or denied read access to this
  // environment's databases.
  "mcp.client_authorized": Plug,
  "mcp.client_revoked": Unplug,
};

function pickIcon(action: string): LucideIcon {
  // `Edit2` inline rather than behind a named constant: a module-level binding
  // holding a component is what makes this file look like a component module,
  // and it exports a formatter instead.
  return ICON_BY_ACTION[action] ?? Edit2;
}

function targetLabel(item: ActivityItem, fallback = "?"): string {
  return item.target.label ?? fallback;
}

function detail<T = unknown>(item: ActivityItem, key: string): T | undefined {
  return item.details?.[key] as T | undefined;
}

function formatTime(iso: string): string {
  const t = new Date(iso);
  const now = Date.now();
  const diffMs = now - t.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)} min ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)} h ago`;
  if (diffMs < 2 * day) {
    return `Yesterday · ${t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return t.toLocaleDateString([], { month: "short", day: "numeric" });
}

const s = (text: string) => <strong>{text}</strong>;

/** The client an MCP entry is about, by the label or the details' copy of it. */
function mcpClientName(item: ActivityItem): string {
  return item.target.label ?? detail<string>(item, "client_name") ?? "?";
}

// A revoke an admin pressed for this one client is the plain sentence, so
// `admin_revoke` has no words of its own.
const MCP_REVOKE_CAUSES: Record<string, string> = {
  admin_revoke_all: "Revoke all",
  user_disabled: "account disabled",
  user_demoted: "no longer a super admin",
  password_changed: "password changed",
  signed_out_everywhere: "signed out everywhere",
  client_revoked_token: "revoked by the app",
  code_replay: "authorization code reused",
  refresh_reuse: "refresh token reused",
  access_lost: "access lost",
};

/** Why an MCP grant went, in words — or the server's own code when it is new. */
function mcpRevokeCause(item: ActivityItem): string | null {
  const reason = detail<string>(item, "reason");
  if (typeof reason === "string" && reason !== "" && reason !== "admin_revoke") {
    return MCP_REVOKE_CAUSES[reason] ?? reason;
  }
  return detail<boolean>(item, "revoke_all") === true ? "Revoke all" : null;
}

/** " (412.3 KiB)" — the size the entry recorded, or nothing. */
function saveSize(item: ActivityItem): string {
  const size = numericDetail(item, "size_bytes");
  return size === undefined ? "" : ` (${formatBytes(size)})`;
}

function phraseFor(item: ActivityItem): ReactNode {
  switch (item.action) {
    case "project.created":
      return (
        <>
          created project «{s(detail<string>(item, "name") ?? targetLabel(item))}»
        </>
      );
    case "project.renamed": {
      const oldName = detail<string>(item, "old_name") ?? "";
      const newName = detail<string>(item, "new_name") ?? "";
      return (
        <>
          renamed project «{s(oldName)}» → «{s(newName)}»
        </>
      );
    }
    case "project.suspended":
      return <>suspended project «{s(targetLabel(item))}»</>;
    case "project.unsuspended":
      return <>unsuspended project «{s(targetLabel(item))}»</>;
    case "project.config_updated": {
      const keys = detail<string[]>(item, "changed_keys") ?? [];
      return (
        <>
          updated project config (
          {keys.map((k, i) => (
            <span key={k}>
              {i > 0 ? ", " : ""}
              <code>{k}</code>
            </span>
          ))}
          )
        </>
      );
    }
    case "project.icon_updated":
      return <>updated project icon</>;
    case "api_key.rotated":
      return (
        <>
          rotated API key (ending {s(detail<string>(item, "new_last4") ?? "")})
        </>
      );
    case "invite.sent":
      return <>invited {s(detail<string>(item, "email") ?? targetLabel(item))}</>;
    case "invite.revoked":
      return (
        <>
          revoked invite for {s(detail<string>(item, "email") ?? targetLabel(item))}
        </>
      );
    case "invite.resent":
      return (
        <>
          resent invite to {s(detail<string>(item, "email") ?? targetLabel(item))}
        </>
      );
    case "invite.accepted":
      return <>accepted invite</>;
    case "team.member_removed":
      return (
        <>
          removed {s(detail<string>(item, "email") ?? targetLabel(item))} from project
        </>
      );
    case "player.updated": {
      const changes = (detail<string[]>(item, "changes") ?? []).join(", ");
      const suffix = changes ? ` (${changes})` : "";
      return (
        <>
          updated player {s(targetLabel(item))}
          {suffix}
        </>
      );
    }
    case "player.deleted": {
      // The guild outcome is the half of this event nobody can look up
      // afterwards: the player row is gone, and so is the membership that
      // would have said which guild it was.
      const outcome = detail<string>(item, "guild");
      const guildName = detail<string>(item, "guild_name");
      const successor = detail<string>(item, "successor");
      let suffix = "";
      if (outcome === "transfer" && guildName) {
        suffix = ` — ${guildName} now led by ${successor ?? "another member"}`;
      } else if (outcome === "disband" && guildName) {
        suffix = ` — ${guildName} was disbanded`;
      }
      return (
        <>
          deleted player {s(targetLabel(item))}
          {suffix}
        </>
      );
    }
    case "player.cloud_save_downloaded":
      return (
        <>
          downloaded the cloud save of {s(targetLabel(item))}
          {saveSize(item)}
        </>
      );
    case "player.cloud_save_uploaded": {
      const replaced = detail<boolean>(item, "replaced");
      const size = numericDetail(item, "size_bytes");
      // An entry that does not say whether it replaced anything says
      // nothing about it here either, as the size beside it does.
      const facts = [
        size === undefined ? null : formatBytes(size),
        replaced === undefined
          ? null
          : replaced
            ? "replacing the previous one"
            : "seeding an empty slot",
      ].filter(Boolean);
      return (
        <>
          uploaded a cloud save for {s(targetLabel(item))}
          {facts.length > 0 ? ` (${facts.join(", ")})` : ""}
        </>
      );
    }
    case "player.cloud_save_deleted":
      return (
        <>
          deleted the cloud save of {s(targetLabel(item))}
          {saveSize(item)}
        </>
      );
    case "guild.updated": {
      const changes = (detail<string[]>(item, "changes") ?? []).join(", ");
      const suffix = changes ? ` (${changes})` : "";
      return (
        <>
          updated guild «{s(targetLabel(item))}»{suffix}
        </>
      );
    }
    case "guild.deleted": {
      // The guild row is gone, so this entry is all that is left of it: the
      // name was read before the delete, and the member count is how many
      // players it took with it. Both are read as maybes — an older or
      // hand-written entry must not break the feed.
      const members = numericDetail(item, "member_count");
      const suffix =
        members === undefined
          ? ""
          : ` · ${members} ${members === 1 ? "member" : "members"}`;
      return (
        <>
          deleted guild «{s(targetLabel(item))}»{suffix}
        </>
      );
    }
    case "guild.member_promoted":
      return (
        <>
          promoted {s(detail<string>(item, "player_display") ?? "?")} to officer in «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.member_demoted":
      return (
        <>
          demoted {s(detail<string>(item, "player_display") ?? "?")} to member in «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.leadership_transferred":
      return (
        <>
          made {s(detail<string>(item, "player_display") ?? "?")} leader of «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.member_kicked":
      return (
        <>
          kicked {s(detail<string>(item, "player_display") ?? "?")} from «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.player_unblocked":
      return (
        <>
          unblocked {s(detail<string>(item, "player_display") ?? "?")} in «
          {s(targetLabel(item))}»
        </>
      );
    case "chat.message_posted":
      return <>posted system message in «{s(targetLabel(item))}»</>;
    case "chat.message_deleted": {
      const author = detail<string>(item, "author_display");
      const suffix = author ? ` (${author})` : "";
      return (
        <>
          deleted a message in «{s(targetLabel(item))}»{suffix}
        </>
      );
    }
    case "project.snapshot_exported":
      return <>exported a project snapshot</>;
    case "project.snapshot_applied": {
      // The apply handler writes its total under `changes`. It is rendered
      // when present and skipped when not — an older or hand-written entry
      // must not break the feed.
      const changes = numericDetail(item, "changes");
      const suffix =
        changes === undefined
          ? ""
          : ` · ${changes} ${changes === 1 ? "change" : "changes"}`;
      return (
        <>
          applied a project snapshot{suffix}
        </>
      );
    }
    case "leaderboard.deleted": {
      // The board itself is gone, so this row is all that is left of it: the
      // name is what a human recognises, and the ext_key is the handle another
      // board is now free to claim. Every field is read as a maybe — an older
      // or hand-written entry must not break the feed.
      const extKey = detail<string>(item, "ext_key");
      const cycles = numericDetail(item, "cycles");
      const scoreRows = numericDetail(item, "score_rows");
      const unclaimed = numericDetail(item, "unclaimed_grants");
      const parts: string[] = [];
      if (cycles !== undefined) {
        parts.push(`${cycles} ${cycles === 1 ? "cycle" : "cycles"}`);
      }
      if (scoreRows !== undefined) {
        parts.push(`${scoreRows} ${scoreRows === 1 ? "score" : "scores"}`);
      }
      // Only when there were any. This half of the phrase is about what players
      // lost, and «0 unclaimed rewards» is the absence of that, not a loss.
      if (unclaimed !== undefined && unclaimed > 0) {
        parts.push(`${unclaimed} unclaimed rewards`);
      }
      return (
        <>
          deleted leaderboard «{s(targetLabel(item))}»
          {extKey ? (
            <>
              {" "}
              (<code>{extKey}</code>)
            </>
          ) : null}
          {parts.length > 0 ? ` · ${parts.join(", ")}` : ""}
        </>
      );
    }
    case "leaderboard.pending_scheduled":
    case "leaderboard.pending_cancelled":
    case "leaderboard.pending_applied":
    case "leaderboard.pending_refused": {
      // The package row is resolved — or deleted — by the time anybody reads
      // this, so `fields` here is the only surviving account of what the
      // change was. Every part is read as a maybe: an older or hand-written
      // entry must not break the feed.
      const fields = detail<Record<string, unknown>>(item, "fields");
      const cancelled = detail<string[]>(item, "cancelled_fields");
      const rewards = numericDetail(item, "rewards");
      const names = Array.isArray(cancelled)
        ? cancelled
        : fields && typeof fields === "object"
          ? Object.keys(fields)
          : [];
      const changed = names.length + (rewards === undefined ? 0 : 1);
      const count = `${changed} ${changed === 1 ? "change" : "changes"}`;
      const board = (
        <>
          «{s(targetLabel(item))}»
        </>
      );
      if (item.action === "leaderboard.pending_scheduled") {
        return (
          <>
            scheduled {count} on {board} for the next cycle
            {names.length > 0 ? ` · ${names.join(", ")}` : ""}
          </>
        );
      }
      if (item.action === "leaderboard.pending_cancelled") {
        // A partial cancellation names the lines it struck; a whole one has
        // nothing to name, because the package went with them.
        return Array.isArray(cancelled) && cancelled.length > 0 ? (
          <>
            cancelled {cancelled.join(", ")} from the scheduled changes on {board}
          </>
        ) : (
          <>cancelled the scheduled changes on {board}</>
        );
      }
      if (item.action === "leaderboard.pending_applied") {
        return (
          <>
            applied {count} to {board} at the cycle cut
            {names.length > 0 ? ` · ${names.join(", ")}` : ""}
          </>
        );
      }
      const reason = detail<string>(item, "failure_reason");
      const field = detail<string>(item, "failure_field");
      return (
        <>
          scheduled changes on {board} were refused at the cycle cut
          {field ? (
            <>
              {" "}
              (<code>{field}</code>)
            </>
          ) : null}
          {reason ? ` · ${reason}` : ""}
        </>
      );
    }
    case "project.wiped": {
      // Of every action in this feed, this is the one whose evidence is most
      // thoroughly gone: the players and guilds it counts were deleted by the
      // very operation being recorded, and nothing in the database can be
      // asked how many there were. So the details carry the figures, and — as
      // with its destructive neighbours — every one of them is read as a
      // maybe: an older or hand-written entry must not break the feed.
      const outcome = detail<string>(item, "outcome");
      /**
       * Why it stopped — written only by the reaper, for a wipe whose process
       * died rather than one that threw.
       *
       * Without it both endings print the same sentence, and they call for
       * opposite things: a task the server took down with it is a task to
       * start again, a deletion that hit a constraint is a schema to go and
       * look at. The task row that could have told them apart is deleted with
       * the project one day, so this entry is where the difference has to
       * survive — which is the reason the record carries the key at all.
       *
       * Read on the failure branch alone: a wipe that finished has no reason
       * to give, and the recorder writes none.
       */
      const reason = detail<string>(item, "reason");
      const players = numericDetail(item, "players");
      const guilds = numericDetail(item, "guilds");
      const rows = numericDetail(item, "rows");
      const durationMs = numericDetail(item, "duration_ms");
      // `num` and not the raw figures its neighbours print: theirs are guild
      // rosters and cycle counts, two digits of them, while this entry is the
      // one carrying the seven-digit totals of a whole project — and the same
      // totals the dialog, the toast and the "Removed" rows all group.
      const parts: string[] = [];
      if (players !== undefined) {
        parts.push(`${num(players)} ${players === 1 ? "player" : "players"}`);
      }
      if (guilds !== undefined) {
        parts.push(`${num(guilds)} ${guilds === 1 ? "guild" : "guilds"}`);
      }
      if (rows !== undefined) {
        parts.push(`${num(rows)} ${rows === 1 ? "row" : "rows"}`);
      }
      if (durationMs !== undefined) {
        parts.push(`took ${formatDuration(durationMs)}`);
      }
      // A wipe that stopped is not a footnote on a successful one: it left the
      // project half empty, and this row is where somebody finds that out
      // weeks later. The verb says the task ran and ended on its own — an
      // operator cannot stop one, so nobody should read this as somebody
      // changing their mind.
      return (
        <>
          {outcome === "failed" ? (
            <>
              ran a wipe of project «{s(targetLabel(item))}» that stopped
              halfway
              {/* The server's own words, passed through and not paraphrased:
                  it is the writer of this key that knows whether the process
                  was killed or the deletion refused. */}
              {reason ? ` — ${reason}` : ""}
            </>
          ) : (
            <>wiped project «{s(targetLabel(item))}»</>
          )}
          {parts.length > 0 ? ` · ${parts.join(", ")}` : ""}
        </>
      );
    }
    case "mcp.client_authorized": {
      const redirect = detail<string>(item, "redirect_host");
      return (
        <>
          authorized MCP client «{s(mcpClientName(item))}»
          {redirect ? ` (${redirect})` : ""}
        </>
      );
    }
    case "mcp.client_revoked": {
      // The owner is named because any super admin may revoke anyone's client,
      // and the cause because most revocations happen without anyone pressing
      // Revoke at all.
      const owner =
        detail<string>(item, "user_name") ?? detail<string>(item, "user_email");
      const redirect = detail<string>(item, "redirect_host");
      const cause = mcpRevokeCause(item);
      return (
        <>
          revoked MCP client «{s(mcpClientName(item))}»
          {redirect ? ` (${redirect})` : ""}
          {owner ? ` of ${owner}` : ""}
          {cause ? ` · ${cause}` : ""}
        </>
      );
    }
    default:
      return (
        <>
          performed unknown action <code>{item.action}</code>
        </>
      );
  }
}

export function renderEntry(item: ActivityItem): RenderedEntry {
  return {
    icon: pickIcon(item.action),
    phrase: phraseFor(item),
    time: formatTime(item.created_at),
  };
}
